import React, { useState, useEffect } from 'react';
import { Inbox, TrendingUp, Calendar, Phone, Mail, MessageSquare, Home, ExternalLink } from 'lucide-react';
import s from './adminStyles';
import { authHeaders } from '../../context/AuthContext';
import Sparkline from './Sparkline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new: { bg: '#FEF3C7', fg: '#92400E' },
  contacted: { bg: '#DBEAFE', fg: '#1E40AF' },
  forwarded: { bg: '#E0E7FF', fg: '#3730A3' },
  viewing_scheduled: { bg: '#FAE8FF', fg: '#86198F' },
  negotiating: { bg: '#FED7AA', fg: '#9A3412' },
  sold: { bg: '#DCFCE7', fg: '#166534' },
  closed: { bg: '#E5E7EB', fg: '#374151' },
};

const CTA_META = {
  info: { label: 'Info-aanvraag', icon: Mail, color: '#C5A059' },
  viewing: { label: 'Bezichtiging', icon: Calendar, color: '#A855F7' },
  callback: { label: 'Terugbelverzoek', icon: Phone, color: '#3B82F6' },
  newsletter: { label: 'Newsletter', icon: MessageSquare, color: '#8C8B88' },
  homepage: { label: 'Homepage', icon: MessageSquare, color: '#8C8B88' },
};

const formatPrice = (n) => n ? `€${Number(n).toLocaleString('nl-NL')}` : '—';

const formatRelative = (iso) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'vandaag';
  if (days === 1) return 'gisteren';
  if (days < 7) return `${days} dagen geleden`;
  if (days < 30) return `${Math.floor(days / 7)} wk geleden`;
  return `${Math.floor(days / 30)} mnd geleden`;
};

const AnalyticsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/admin/stats`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setStats(d); setLoading(false); })
      .catch(e => { console.error(e); setLoading(false); });
  }, []);

  if (loading) return <p style={d.muted}>Statistieken laden...</p>;
  if (!stats) return <p style={d.muted}>Geen data beschikbaar.</p>;

  const { leads, status_breakdown, status_options, top_properties, cta_breakdown, daily_leads } = stats;

  // Calculate status percentages for the bar
  const statusEntries = Object.entries(status_options)
    .map(([slug, label]) => ({ slug, label, count: status_breakdown[slug] || 0 }))
    .filter(e => e.count > 0);
  const statusTotal = statusEntries.reduce((sum, e) => sum + e.count, 0);

  return (
    <div data-testid="admin-dashboard">
      <div style={d.headerRow}>
        <h2 style={s.sectionTitle}>Dashboard</h2>
        <p style={d.subtitle}>
          Live overzicht van leads en aanvragen. Voor verkeer- en bezoekersdata: ga naar{' '}
          <a
            href="https://analytics.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={d.link}
            data-testid="ga-link"
          >
            Google Analytics <ExternalLink size={12} style={{ display: 'inline', verticalAlign: 'middle' }} />
          </a>
        </p>
      </div>

      {/* KPI cards */}
      <div style={d.kpiGrid}>
        <KpiCard
          icon={Inbox}
          label="Totaal leads"
          value={leads.total}
          accent="#1A1918"
          testId="kpi-total-leads"
        />
        <KpiCard
          icon={TrendingUp}
          label="Vandaag"
          value={leads.today}
          accent="#C5A059"
          testId="kpi-leads-today"
        />
        <KpiCard
          icon={Calendar}
          label="Laatste 7 dagen"
          value={leads.last_7_days}
          accent="#22C55E"
          testId="kpi-leads-7d"
        />
        <KpiCard
          icon={Calendar}
          label="Laatste 30 dagen"
          value={leads.last_30_days}
          accent="#3B82F6"
          testId="kpi-leads-30d"
        />
      </div>

      {/* Sparkline trend — last 30 days */}
      {daily_leads && daily_leads.length > 0 && (
        <div style={d.card} data-testid="sparkline-card">
          <h3 style={d.cardTitle}>Leads per dag — laatste 30 dagen</h3>
          <Sparkline data={daily_leads} />
        </div>
      )}

      {/* Status breakdown */}
      <div style={d.card} data-testid="status-breakdown-card">
        <h3 style={d.cardTitle}>Lead-status pijplijn</h3>
        {statusTotal > 0 ? (
          <>
            <div style={d.statusBar}>
              {statusEntries.map(({ slug, count }) => {
                const color = STATUS_COLORS[slug] || STATUS_COLORS.new;
                const pct = (count / statusTotal) * 100;
                return (
                  <div
                    key={slug}
                    style={{ ...d.statusBarSegment, width: `${pct}%`, backgroundColor: color.fg }}
                    title={`${status_options[slug]}: ${count}`}
                  />
                );
              })}
            </div>
            <div style={d.statusList}>
              {statusEntries.map(({ slug, label, count }) => {
                const color = STATUS_COLORS[slug] || STATUS_COLORS.new;
                return (
                  <div key={slug} style={d.statusRow} data-testid={`status-row-${slug}`}>
                    <span style={{ ...d.statusBadge, backgroundColor: color.bg, color: color.fg }}>
                      {label}
                    </span>
                    <span style={d.statusCount}>{count}</span>
                    <span style={d.statusPct}>
                      {((count / statusTotal) * 100).toFixed(0)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p style={d.muted}>Nog geen leads ontvangen.</p>
        )}
      </div>

      {/* Top properties by leads */}
      <div style={d.card} data-testid="top-properties-card">
        <h3 style={d.cardTitle}>Top 5 woningen — meeste aanvragen</h3>
        {top_properties && top_properties.length > 0 ? (
          <div style={d.topList}>
            {top_properties.map((p, i) => (
              <a
                key={p.property_id}
                href={`/woning/${p.property_id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={d.topRow}
                data-testid={`top-property-${i}`}
              >
                <span style={d.topRank}>#{i + 1}</span>
                <span style={d.topMain}>
                  <span style={d.topTitle}>
                    <Home size={14} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
                    {p.title || `Property ${p.property_id}`}
                  </span>
                  <span style={d.topMeta}>
                    {p.location} {p.price && `· ${formatPrice(p.price)}`} {p.ref && `· Ref. ${p.ref}`}
                  </span>
                </span>
                <span style={d.topLeads} title="Aantal aanvragen">
                  <Inbox size={13} /> {p.leads}
                </span>
                <span style={d.topDate}>{formatRelative(p.last_lead_at)}</span>
              </a>
            ))}
          </div>
        ) : (
          <p style={d.muted}>Nog geen woningaanvragen.</p>
        )}
      </div>

      {/* CTA type breakdown */}
      <div style={d.card} data-testid="cta-breakdown-card">
        <h3 style={d.cardTitle}>Aanvragen per type</h3>
        <div style={d.ctaGrid}>
          {Object.entries(cta_breakdown || {}).map(([cta, count]) => {
            const meta = CTA_META[cta] || { label: cta, icon: MessageSquare, color: '#8C8B88' };
            const Icon = meta.icon;
            return (
              <div key={cta} style={d.ctaCard} data-testid={`cta-stat-${cta}`}>
                <Icon size={20} color={meta.color} />
                <div>
                  <div style={d.ctaCount}>{count}</div>
                  <div style={d.ctaLabel}>{meta.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div style={d.kpiCard} data-testid={testId}>
    <div style={{ ...d.kpiIconWrap, backgroundColor: `${accent}15` }}>
      <Icon size={20} color={accent} />
    </div>
    <div>
      <div style={d.kpiValue}>{value}</div>
      <div style={d.kpiLabel}>{label}</div>
    </div>
  </div>
);

const d = {
  headerRow: { marginBottom: '1.5rem' },
  subtitle: { color: '#8C8B88', fontSize: '0.88rem', margin: '0.5rem 0 0' },
  link: { color: '#C5A059', textDecoration: 'underline' },
  muted: { color: '#8C8B88', fontSize: '0.9rem', fontStyle: 'italic' },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '0.85rem',
    marginBottom: '1.5rem',
  },
  kpiCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.85rem',
    padding: '1.25rem',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
  },
  kpiIconWrap: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  kpiValue: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.85rem',
    color: '#1A1918',
    lineHeight: 1.1,
  },
  kpiLabel: { color: '#8C8B88', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.25rem' },
  card: {
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  cardTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.15rem',
    color: '#1A1918',
    margin: '0 0 1rem',
  },
  statusBar: {
    display: 'flex',
    height: '8px',
    width: '100%',
    overflow: 'hidden',
    backgroundColor: '#F5F1E8',
    marginBottom: '1rem',
  },
  statusBarSegment: { height: '100%' },
  statusList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  statusRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(160px, 1fr) auto auto',
    gap: '0.85rem',
    alignItems: 'center',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '0.3rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    borderRadius: '999px',
    width: 'fit-content',
  },
  statusCount: { color: '#1A1918', fontSize: '0.95rem', fontWeight: 600, textAlign: 'right', minWidth: '32px' },
  statusPct: { color: '#8C8B88', fontSize: '0.82rem', minWidth: '42px', textAlign: 'right' },
  topList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  topRow: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.85rem 1rem',
    backgroundColor: '#FAF9F6',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    color: '#1A1918',
    textDecoration: 'none',
    transition: 'border-color 0.15s ease',
  },
  topRank: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.1rem',
    color: '#C5A059',
    width: '28px',
  },
  topMain: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  topTitle: {
    color: '#1A1918',
    fontSize: '0.9rem',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  topMeta: { color: '#8C8B88', fontSize: '0.78rem', marginTop: '0.15rem' },
  topLeads: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    backgroundColor: '#1A1918',
    color: '#C5A059',
    padding: '0.3rem 0.6rem',
    fontSize: '0.82rem',
    fontWeight: 600,
  },
  topDate: { color: '#8C8B88', fontSize: '0.75rem', whiteSpace: 'nowrap' },
  ctaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.75rem',
  },
  ctaCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '1rem',
    backgroundColor: '#FAF9F6',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
  },
  ctaCount: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.4rem',
    color: '#1A1918',
    lineHeight: 1,
  },
  ctaLabel: { color: '#8C8B88', fontSize: '0.78rem', marginTop: '0.2rem' },
};

export default AnalyticsPanel;
